<template>
    <div class="floating-chat mb-5 mb-md-5 mr-md-3 new-message-bg" @click="openElement">
        <i class="fa fa-comments" aria-hidden="true"></i>
        <div class="badge bg-danger" v-if="unreadMessages > 0">
            {{ unreadMessages > 99 ? '99+' : unreadMessages }}
        </div>
        <div class="chat">
            <div class="header p-0 bg-primary d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center p-2">
                    <img src="@/assets/img/firefly.png" alt="Firefly" class="chat-logo">
                    <span class="title text-white">Firefly by Fintrens</span>
                </div>
                <div class="d-flex align-items-center">
                    <button @click.stop="downloadEventsFile" class="btn btn-link text-white px-2" title="Download">
                        <i class="fa fa-download"></i>
                    </button>
                    <button @click.stop="enableSound" class="btn btn-link text-white px-2">
                        <i :class="soundEnabled ? 'fa fa-bell' : 'fa fa-bell-slash'"></i>
                    </button>
                    <button @click.stop="closeElement">
                        <i class="fa fa-times" aria-hidden="true"></i>
                    </button>
                </div>
            </div>
            <ul class="messages ml-1">
                <li v-if="messages.length === 0" class="text-muted font-weight-bold no-message mt-9 ml-5">
                    No new messages at this time.
                </li>
                <li v-for="(message, index) in messages" :key="index"
                    :class="['other w-100 text-wrap text-black pb-3', message.type, message.severityClass]">
                    <img src="img/theme/firefly-logo.png" alt="Avatar" class="avatar">
                    <span class="mb-2 text-dark">{{ message.text }}</span>
                    <span class="message-time text-dark bottom-0 position-absolute text-nowrap mt-7 align-self-end">{{
                        formatTime(message.time) }}</span>
                </li>
            </ul>
        </div>
        <audio id="notificationSound" :src="notificationSrc" preload="auto"></audio>
    </div>
</template>
<script>
import { registerSubscriber, disconnect } from './statusWebsocket';
import moment from 'moment';
export default {
    data() {
        return {
            messages: [],
            unreadMessages: 0,
            isChatOpen: false,
            element: null,
            timeInterval: null,
            notificationSrc: require('@/assets/sound/notify.wav'),
            soundEnabled: JSON.parse(localStorage.getItem('ffSoundEnabled') || 'false'),
        };
    },
    computed: {
        unreadMessages() {
            return this.messages.filter(message => !message.isRead).length;
        },
    },
    mounted() {
        this.element = this.$el;
        this.loadMessagesFromSession();
        if (!localStorage.getItem('chatID')) {
            localStorage.setItem('chatID', this.createUUID());
        }
        setTimeout(() => {
            this.element.classList.add('enter');
        }, 1000);
    },
    created() {
        registerSubscriber(this.onMessageReceived);
    },
    methods: {
        onMessageReceived(receivedMessage) {
            if (Array.isArray(receivedMessage)) {
                const icon = this.element.querySelector('i');
                const currentTime = new Date().getTime();
                receivedMessage.forEach(message => {
                    const severityClass = this.getSeverityClass(message.severityType);
                    this.messages.push({
                        type: 'other',
                        text: message.message,
                        time: currentTime,
                        isRead: this.isChatOpen,
                        severityClass: severityClass,
                    });
                    this.playNotificationSound();
                });
                if (!this.isChatOpen) {
                    if (icon) {
                        icon.classList.add('shake');
                        setTimeout(() => {
                            icon.classList.remove('shake');
                        }, 500);
                    }
                }
                this.clearExpiredMessages();
                this.scrollToBottom(this.element.querySelector('.messages'));
                this.saveMessagesToSession();
            }
        },
        getSeverityClass(severity) {
            switch (severity) {
                case 'CRITICAL':
                    return 'bg-danger-chat';
                case 'INFO':
                    return 'bg-info-chat';
                case 'MINOR':
                    return 'bg-warning-chat';
                case 'MAJOR':
                    return 'bg-danger-chat';
                default:
                    return '';
            }
        },
        openElement() {
            const messages = this.element.querySelector('.messages');
            const icon = this.element.querySelector('i');
            const badge = this.element.querySelector('.badge');
            if (icon) icon.style.display = 'none';
            if (badge) badge.style.display = 'none';
            this.isChatOpen = true;
            this.messages.forEach(message => {
                message.isRead = true;
            });
            this.element.classList.add('expand');
            this.element.querySelector('.chat').classList.add('enter');
            this.scrollToBottom(messages);
            this.saveMessagesToSession();
        },
        closeElement() {
            const chatElement = this.element.querySelector('.chat');
            const icon = this.element.querySelector('i');
            const badge = this.element.querySelector('.badge');
            chatElement.classList.remove('enter');
            if (icon) icon.style.display = 'block';
            if (badge) badge.style.display = 'block';
            this.isChatOpen = false;
            this.element.classList.remove('expand');
            setTimeout(() => {
                chatElement.classList.remove('enter');
                this.element.addEventListener('click', this.openElement);
            }, 500);
        },
        enableSound() {
            this.soundEnabled = !this.soundEnabled;
            localStorage.setItem('ffSoundEnabled', JSON.stringify(this.soundEnabled));
        },
        playNotificationSound() {
            if (!this.soundEnabled || this.isChatOpen) {
                return;
            }
            const sound = document.getElementById('notificationSound');
            if (!sound) return;
            try {
                sound.currentTime = 0;
                sound.play().catch((e) => {
                    console.warn('Could not play notification sound:', e);
                });
            } catch (e) {
                console.warn('Error playing notification sound:', e);
            }
        },
        createUUID() {
            let s = [];
            const hexDigits = "0123456789abcdef";
            for (let i = 0; i < 36; i++) {
                s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
            }
            s[14] = "4";
            s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1);
            s[8] = s[13] = s[18] = s[23] = "-";
            return s.join("");
        },
        scrollToBottom(element) {
            element.scrollTop = element.scrollHeight;
        },
        saveMessagesToSession() {
            sessionStorage.setItem('messages', JSON.stringify(this.messages));
        },
        loadMessagesFromSession() {
            const savedMessages = sessionStorage.getItem('messages');
            if (savedMessages) {
                this.messages = JSON.parse(savedMessages);
            }
        },
        clearExpiredMessages() {
            const hoursLimit = new Date().getTime() - 7200000;
            const filteredMessages = this.messages.filter(message => message.time >= hoursLimit);
            if (filteredMessages) {
                this.messages = filteredMessages;
                this.saveMessagesToSession();
            }
        },
        formatTime(timestamp) {
            return moment(timestamp).format('DD/MM/YYYY, hh:mm A');
        },
        downloadEventsFile() {
            this.$store
                .dispatch("commonStore/getAllMessages", null, { root: true })
                .then((response) => {
                    const events = Array.isArray(response) ? response : response?.server?.data ?? response?.data;
                    const text = this.buildEventsText(events);
                    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `firefly-events-${moment().format("YYYY-MM-DD")}.txt`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                })
                .catch((error) => {
                    console.error("Error downloading events file:", error);
                });
        },
        buildEventsText(events) {
            const sorted = [...events].sort((a, b) => {
                const ta = new Date(String(a.time).replace(" ", "T")).getTime();
                const tb = new Date(String(b.time).replace(" ", "T")).getTime();
                return (isNaN(ta) ? 0 : ta) - (isNaN(tb) ? 0 : tb);
            });
            const groups = new Map();
            for (const e of sorted) {
                const date = String(e.time || "").split(" ")[0] || "UNKNOWN-DATE";
                if (!groups.has(date)) groups.set(date, []);
                groups.get(date).push(e);
            }
            let out = "";
            for (const [date, list] of groups.entries()) {
                out += `${date}\n`;
                out += "─".repeat(60) + "\n\n";
                for (const e of list) {
                    const timePart = (String(e.time || "").split(" ")[1] || "").substring(0, 8) || "??:??:??";
                    out += `${timePart} ${e.severityType || "INFO"}: ${(e.message ?? "").toString()}\n\n`;
                }
                out += `\n`;
            }
            return out.trimEnd() + "\n";
        },
    },
    beforeDestroy() {
        disconnect();
        if (this.timeInterval) {
            clearInterval(this.timeInterval);
        }
    },
};
</script>
